/**
 * Tiny pub/sub for cross-component messaging.
 *
 * The inline PDP section and the floating modal live in different shadow
 * trees (different mount points, different positioning contexts). They
 * need to share two pieces of state:
 *
 *   1. "Open the modal" — when a shopper clicks a chip in the inline
 *      section, the modal should fly open.
 *   2. "Send this message" — the inline-section chip click or input-send
 *      should auto-fire as the first message in the modal.
 *
 * This keeps state external to Preact so both shadow roots can subscribe
 * without context plumbing across boundaries.
 */

type Listener = () => void;

interface ChatRequest {
  text: string;
  // Optional: a hint about which product the question is about. Currently
  // unused but plumbed through so future tools can take advantage.
  productHandle?: string;
}

let pendingRequest: ChatRequest | null = null;
const listeners = new Set<Listener>();

function notify(): void {
  for (const fn of listeners) fn();
}

export const chatStore = {
  /** Submit a chat request. The modal will pick this up and open. */
  setRequest(req: ChatRequest): void {
    pendingRequest = req;
    notify();
  },

  /**
   * Read the pending request and clear it (one-shot consume). Returns null
   * if no pending request. The modal calls this in a useEffect so each
   * `setRequest` triggers exactly one send.
   */
  consumeRequest(): ChatRequest | null {
    const r = pendingRequest;
    pendingRequest = null;
    return r;
  },

  subscribe(fn: Listener): () => void {
    listeners.add(fn);
    return () => listeners.delete(fn);
  },
};

/**
 * Detect whether we're currently on a Shopify product detail page. We
 * sniff the URL path: `/products/<handle>` or `/products/<handle>?...`.
 * Returns the handle string or null.
 */
export function detectProductHandle(): string | null {
  if (typeof window === "undefined") return null;
  const path = window.location.pathname;
  // Match `/products/<handle>` and `/<locale>/products/<handle>` and
  // `/<store-prefix>/products/<handle>` — anything ending in /products/<slug>.
  const m = path.match(/\/products\/([a-z0-9][a-z0-9-_]*)\/?$/i);
  if (!m) return null;
  // A dead product URL still matches the path pattern but renders the 404
  // template — treating it as a PDP makes the widget offer "Ask any question
  // about 404 Not Found". Require the page to actually be a product page.
  if (isNotFoundPage()) return null;
  return m[1];
}

/**
 * Detect Shopify's 404 template. Checked in order of reliability:
 * ShopifyAnalytics pageType (authoritative, but loads late), the theme's
 * `template-404` body class, then the document title.
 */
function isNotFoundPage(): boolean {
  if (typeof document === "undefined") return false;
  const w = window as unknown as {
    ShopifyAnalytics?: { meta?: { page?: { pageType?: string } } };
  };
  const pageType = w.ShopifyAnalytics?.meta?.page?.pageType;
  if (pageType) return pageType === "notFound";
  if (document.body?.classList.contains("template-404")) return true;
  return /^\s*404\b|page not found/i.test(document.title);
}

/**
 * Pull the product title from the page if available. Most Shopify themes
 * expose this via meta tags or a window-level analytics object. We try a
 * few sources; fall back to null.
 */
export function detectProductTitle(): string | null {
  if (typeof document === "undefined") return null;

  // Open Graph title — most themes set this on PDPs
  const og = document.querySelector('meta[property="og:title"]');
  const ogTitle = og?.getAttribute("content");
  if (ogTitle) return ogTitle.trim();

  // Shopify analytics metafield (loaded by the storefront's analytics.js)
  const w = window as unknown as {
    ShopifyAnalytics?: { meta?: { product?: { title?: string } } };
  };
  const sa = w.ShopifyAnalytics?.meta?.product?.title;
  if (sa) return sa;

  // <title> tag fallback — strip " - Store Name" suffix if present
  const t = document.title;
  if (t) return t.replace(/\s*[-—|]\s*[^|—-]+$/, "").trim();

  return null;
}
